import { BlankSlate } from "@msuite/ui/blank-slate";
import { LoadingScreen } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { Tabs } from "@msuite/ui/tabs";
import { ResponseError } from "@msuite/utils/errors";
import { useRole } from "@/hooks/use-role";
import { PermissionEditor } from "./permission-editor";
import { RoleMembers } from "./role-members";

/** Props Interface */
interface RoleDetailPanelProps {
	roleId: string;
}

/**
 * The right half of the roles surface: the selected role under two tabs, permissions and
 * members — the same tab pattern as the settings above it. No title card: the role's name is
 * marked in the list beside the panel, and the tabs carry the rest. The selection lives in that
 * list, so a role that vanishes under the reader — deleted in another tab, or the one they just
 * deleted — needs no way back, only a note; the list re-selects on its own.
 */
export function RoleDetailPanel({ roleId }: RoleDetailPanelProps) {
	/** Context */
	const { t } = useTranslation();

	/** Query */
	const { role, isPending, error } = useRole(roleId);

	/** Computed */
	// A direct link can reach a role the caller may not manage; "this role no longer exists"
	// would be a lie about a role that is doing fine, so the refusal is told apart by its status.
	const isRefused = error instanceof ResponseError && error.status === 403;

	/** Guard */
	if (isPending) return <LoadingScreen />;
	if (!role) {
		return (
			<BlankSlate
				label={t(
					isRefused ? "permissions.you_are_not_authorized_to_access_this_page" : "roles.not_found",
				)}
				data-cy="role-not-found"
			/>
		);
	}

	/** Render */
	// The x-axis is clipped because the tabs bleed eight pixels into either gutter (`-mx-2`) —
	// only padding lives out there, and a scroll container that leaves the axis open answers the
	// bleed with a scrollbar a few millimetres wide. Sideways scrolling belongs to the table.
	return (
		<div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden v-stack bottom-space">
			<Tabs
				panelKey="role-tabs"
				groups={[
					{
						name: "role",
						panels: [
							{
								label: t("permissions.permissions"),
								panel: () => (
									<PermissionEditor
										roleId={roleId}
										role={role}
									/>
								),
								errors: null,
								cyId: "role-permissions-tab",
							},
							{
								label: t("organization.members"),
								count: role.memberCount,
								panel: () => (
									<RoleMembers
										roleId={roleId}
										role={role}
									/>
								),
								errors: null,
								cyId: "role-members-tab",
							},
						],
					},
				]}
			/>
		</div>
	);
}
